import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AibaLogo } from "../components/site/Logo";
import { useAuth } from "../lib/AuthContext";
import {
  accountOverview, accountPurchases, accountProducts, accountPayments, accountGenerations,
  updateProfile, changePassword, uploadFile, absFileUrl, downloadProduct,
  backupStatus, runBackup, adminStats, connectStatus, connectOnboard, connectWithdraw, formatApiError,
} from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, User, Coins, Crown, Sparkles, ShoppingBag, Store, Receipt,
  Loader2, Save, Camera, Lock, Download, LogOut, ImageIcon, FileText, AudioLines, Video,
  Gift, Check, ExternalLink, Database,
  ShieldCheck, TrendingUp, Users, Film, MessageSquare, Wallet, Flag, Landmark, ArrowRight,
} from "lucide-react";

const TABS = [
  { key: "profile", label: "Profil", icon: User },
  { key: "generations", label: "Générations", icon: Sparkles },
  { key: "purchases", label: "Achats", icon: ShoppingBag },
  { key: "products", label: "Mes produits", icon: Store },
  { key: "affiliation", label: "Affiliation", icon: Gift },
  { key: "payments", label: "Paiements", icon: Receipt },
];

const TIER_COLOR = {
  Gratuit: "text-[#8AA0C0] border-blue-500/20 bg-blue-500/5",
  Débutant: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  Expert: "text-blue-300 border-blue-400/40 bg-blue-400/10",
  Premium: "text-amber-300 border-amber-400/40 bg-amber-400/10",
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-5 backdrop-blur-xl">
    <Icon size={18} className="text-blue-300" />
    <p className="mt-3 font-display text-3xl font-800 tracking-tighter text-white">{value}</p>
    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#8AA0C0]">{label}</p>
  </div>
);

const ProfileTab = ({ overview, reload }) => {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [picture, setPicture] = useState(user?.picture || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const isGoogle = overview?.provider === "google";

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Image trop lourde (max 6 Mo).");
    try {
      setUploading(true);
      const { data } = await uploadFile(f);
      const url = absFileUrl(data.path);
      setPicture(url);
      // Persist immediately so the new photo is saved without needing to click "Enregistrer".
      const { data: prof } = await updateProfile({ picture: url });
      updateUser({ picture: prof.picture });
      reload();
      toast.success("Photo de profil enregistrée.");
    } catch { toast.error("Upload impossible."); }
    finally { setUploading(false); }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      const { data } = await updateProfile({ name, picture });
      updateUser({ name: data.name, picture: data.picture });
      toast.success("Profil mis à jour.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la mise à jour."); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (pw.next.length < 6) return toast.error("Le nouveau mot de passe doit faire au moins 6 caractères.");
    if (pw.next !== pw.confirm) return toast.error("Les mots de passe ne correspondent pas.");
    try {
      setPwBusy(true);
      await changePassword({ current_password: pw.current, new_password: pw.next });
      toast.success("Mot de passe modifié.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" && d) || "Échec du changement de mot de passe.");
    } finally { setPwBusy(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-7 backdrop-blur-xl" data-testid="account-edit-profile">
        <h3 className="font-display text-lg font-700 tracking-tight text-white">Informations personnelles</h3>
        <div className="mt-6 flex items-center gap-4">
          <div className="relative">
            {picture ? (
              <img src={picture} alt="avatar" className="h-20 w-20 rounded-2xl border border-blue-500/25 object-cover" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-blue-500/25 bg-blue-500/10">
                <User size={30} className="text-blue-300" />
              </div>
            )}
            <label data-testid="account-avatar-label" className="absolute -bottom-2 -right-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-blue-400/40 bg-[#0B1220] text-blue-200 hover:bg-blue-500/20">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
              <input data-testid="account-avatar-input" type="file" accept="image/*" onChange={pickAvatar} className="hidden" />
            </label>
          </div>
          <div>
            <p className="font-body text-sm text-white">{user?.email}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">
              {isGoogle ? "Connexion Google" : "Connexion e-mail"}
            </p>
          </div>
        </div>
        <label className="mt-6 block font-mono text-[10px] uppercase tracking-[0.2em] text-blue-300/70">Nom affiché</label>
        <input
          data-testid="account-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-2 w-full rounded-xl border border-blue-500/20 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40"
        />
        <button data-testid="account-save-profile" onClick={saveProfile} disabled={saving || uploading}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500 disabled:opacity-60">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
        </button>
      </div>

      <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-7 backdrop-blur-xl" data-testid="account-password-card">
        <h3 className="flex items-center gap-2 font-display text-lg font-700 tracking-tight text-white">
          <Lock size={16} className="text-blue-300" /> Mot de passe
        </h3>
        {isGoogle ? (
          <p className="mt-4 font-body text-sm text-[#8AA0C0]">
            Votre compte utilise la connexion Google. La gestion du mot de passe se fait directement via votre compte Google.
          </p>
        ) : (
          <div className="mt-5 space-y-3">
            <input data-testid="account-pw-current" type="password" placeholder="Mot de passe actuel" value={pw.current}
              onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))}
              className="w-full rounded-xl border border-blue-500/20 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
            <input data-testid="account-pw-new" type="password" placeholder="Nouveau mot de passe" value={pw.next}
              onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))}
              className="w-full rounded-xl border border-blue-500/20 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
            <input data-testid="account-pw-confirm" type="password" placeholder="Confirmer le nouveau mot de passe" value={pw.confirm}
              onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))}
              className="w-full rounded-xl border border-blue-500/20 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
            <button data-testid="account-save-password" onClick={savePassword} disabled={pwBusy}
              className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-500/10 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-blue-100 hover:bg-blue-500/20 disabled:opacity-60">
              {pwBusy ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Modifier
            </button>
          </div>
        )}
      </div>
      {user?.role === "admin" && <AdminBackupCard />}
    </div>
  );
};

const AdminBackupCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => backupStatus().then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const backup = async () => {
    try {
      setBusy(true);
      const { data } = await runBackup();
      toast.success(`Sauvegarde réussie : ${data.total_documents} documents copiés vers Supabase.`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" && d) || "Échec de la sauvegarde.");
    } finally { setBusy(false); }
  };

  const last = status?.last;
  return (
    <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-7 backdrop-blur-xl lg:col-span-2" data-testid="admin-backup-card">
      <h3 className="flex items-center gap-2 font-display text-lg font-700 tracking-tight text-white">
        <Database size={16} className="text-blue-300" /> Sauvegarde des données (Supabase)
      </h3>
      <p className="mt-3 font-body text-sm text-[#8AA0C0]">
        Copie l'intégralité de vos données (utilisateurs, produits, paiements, générations…) vers votre projet Supabase. Réalisable à tout moment ; chaque sauvegarde est horodatée.
      </p>
      {status && !status.configured && (
        <p className="mt-3 font-mono text-[11px] text-amber-300">⚠️ Supabase non configuré côté serveur.</p>
      )}
      {last && (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-blue-300/70">
          Dernière : {new Date(last.created_at).toLocaleString("fr-FR")} — {last.total_documents} documents
        </p>
      )}
      <button data-testid="admin-run-backup" onClick={backup} disabled={busy || (status && !status.configured)}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500 disabled:opacity-60">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />} Sauvegarder maintenant
      </button>
    </div>
  );
};

const KIND_ICON = { text: FileText, image: ImageIcon, voice: AudioLines, video: Video };

const GenerationsTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { accountGenerations(24).then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (items === null) return <Loader2 className="mx-auto my-16 animate-spin text-blue-400" />;
  if (items.length === 0) return <Empty text="Aucune génération pour l'instant." />;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="account-generations">
      {items.map((h) => {
        const Icon = KIND_ICON[h.kind] || Sparkles;
        return (
          <div key={h.id} className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-4 backdrop-blur-xl">
            <div className="mb-2 flex items-center gap-2 text-blue-300">
              <Icon size={13} />
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{h.tool_id} · {new Date(h.created_at).toLocaleDateString("fr-FR")}</span>
            </div>
            {h.prompt && <p className="line-clamp-2 font-body text-xs text-[#8AA0C0]">{h.prompt}</p>}
            {h.kind === "image" && h.media && <img src={h.media} alt="" className="mt-2 aspect-square w-full rounded-lg border border-blue-500/20 object-cover" />}
            {h.kind === "text" && h.text && <p className="mt-2 line-clamp-4 whitespace-pre-wrap font-body text-xs text-[#cdd9e8]">{h.text}</p>}
            {h.kind === "voice" && h.media && <audio controls src={h.media} className="mt-2 w-full" />}
            {h.kind === "video" && h.media && (() => {
              const base = h.media.startsWith("http") ? h.media : `${process.env.REACT_APP_BACKEND_URL}${h.media}`;
              const src = base.includes("#") ? base : `${base}#t=0.1`;
              return <video controls preload="metadata" playsInline src={src} className="mt-2 w-full rounded-lg border border-blue-500/20" />;
            })()}
          </div>
        );
      })}
    </div>
  );
};

const PurchasesTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { accountPurchases().then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  const dl = async (p) => {
    try {
      const res = await downloadProduct(p.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = p.title; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Téléchargement impossible."); }
  };
  if (items === null) return <Loader2 className="mx-auto my-16 animate-spin text-blue-400" />;
  if (items.length === 0) return <Empty text="Aucun achat pour l'instant." />;
  return (
    <div className="space-y-3" data-testid="account-purchases">
      {items.map((p) => (
        <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-4 backdrop-blur-xl">
          {p.preview ? <img src={p.preview} alt="" className="h-14 w-14 rounded-lg border border-blue-500/20 object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-blue-500/20 bg-blue-500/5"><Store size={20} className="text-blue-500/40" /></div>}
          <div className="min-w-0 flex-1">
            <p className="truncate font-body text-sm text-white">{p.title}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">par {p.seller_name} · {new Date(p.purchased_at).toLocaleDateString("fr-FR")}</p>
          </div>
          <span className="font-display text-lg font-800 tracking-tighter text-white">${p.price}</span>
          {p.has_file && (
            <button data-testid={`account-download-${p.id}`} onClick={() => dl(p)} className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-200 hover:bg-emerald-400/20">
              <Download size={12} /> Télécharger
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

const PayoutPanel = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");

  const load = () => connectStatus().then(({ data }) => setSt(data)).catch(() => setSt(null));
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    try {
      setBusy(true);
      const { data } = await connectOnboard(window.location.origin);
      window.location.href = data.url;
    } catch { toast.error("Impossible d'ouvrir la configuration Stripe."); setBusy(false); }
  };

  const withdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Entrez un montant valide.");
    try {
      setBusy(true);
      const { data } = await connectWithdraw(amt);
      toast.success(`Versement effectué : $${data.amount_usd.toFixed(2)}`);
      setAmount("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Versement impossible.");
    } finally { setBusy(false); }
  };

  if (!st) return null;
  const ready = st.connected && st.payouts_enabled;

  return (
    <div className="mb-6 rounded-2xl border border-blue-500/20 bg-blue-400/[0.03] p-6 backdrop-blur-xl" data-testid="payout-panel">
      <div className="mb-4 flex items-center gap-2">
        <Landmark size={18} className="text-blue-400" />
        <h3 className="font-display text-lg font-700 tracking-tight text-white">Versements créateur</h3>
        {ready && <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-300"><Check size={11} /> Actif</span>}
      </div>

      <div className="mb-5 grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-blue-500/15 bg-[#0B1220]/40 p-3 text-center">
          <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#8AA0C0]">Gagné</p>
          <p className="mt-1 font-display text-lg font-800 text-white" data-testid="payout-earned">${st.earned_usd.toFixed(2)}</p>
        </div>
        <div className="rounded-xl border border-blue-500/15 bg-[#0B1220]/40 p-3 text-center">
          <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#8AA0C0]">Versé</p>
          <p className="mt-1 font-display text-lg font-800 text-white" data-testid="payout-paid">${st.paid_usd.toFixed(2)}</p>
        </div>
        <div className="rounded-xl border border-emerald-400/25 bg-emerald-400/[0.05] p-3 text-center">
          <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-emerald-300/70">Disponible</p>
          <p className="mt-1 font-display text-lg font-800 text-emerald-300" data-testid="payout-available">${st.available_usd.toFixed(2)}</p>
        </div>
      </div>

      {!ready ? (
        <div>
          <p className="mb-3 font-body text-sm text-[#8AA0C0]">
            {st.connected
              ? "Terminez la vérification Stripe pour recevoir vos versements sur votre compte bancaire."
              : "Reliez votre compte bancaire via Stripe pour recevoir 80 % de vos ventes en argent réel."}
          </p>
          <button data-testid="payout-onboard-btn" onClick={onboard} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow transition-all hover:bg-blue-500 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={14} />}
            {st.connected ? "Terminer la configuration" : "Configurer mes versements"} <ExternalLink size={12} />
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <label className="font-mono text-[9px] uppercase tracking-[0.2em] text-blue-300/70">Montant à retirer ($)</label>
            <input data-testid="payout-amount-input" type="number" min="1" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder={st.available_usd.toFixed(2)}
              className="mt-2 w-full rounded-lg border border-blue-500/25 bg-transparent px-3 py-2.5 font-body text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
          </div>
          <button data-testid="payout-withdraw-btn" onClick={withdraw} disabled={busy || st.available_usd <= 0}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#04140c] transition-all hover:bg-emerald-400 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Retirer
          </button>
        </div>
      )}
    </div>
  );
};

const ProductsTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { accountProducts().then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (items === null) return <Loader2 className="mx-auto my-16 animate-spin text-blue-400" />;
  const totalRevenue = items.reduce((s, p) => s + (p.revenue || 0), 0);
  const totalSales = items.reduce((s, p) => s + p.sales, 0);
  const totalCommission = items.reduce((s, p) => s + (p.commission || 0), 0);
  return (
    <div data-testid="account-products">
      <PayoutPanel />
      {items.length === 0 ? (
        <Empty text="Vous n'avez encore rien mis en vente." />
      ) : (
      <>
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={Coins} label="Revenu net (80%)" value={`$${totalRevenue.toFixed(2)}`} testid="seller-revenue" />
        <StatCard icon={ShoppingBag} label="Ventes" value={totalSales} testid="seller-sales" />
        <StatCard icon={Receipt} label="Commission AIBA (20%)" value={`$${totalCommission.toFixed(2)}`} testid="seller-commission" />
        <StatCard icon={Store} label="Produits" value={items.length} testid="seller-count" />
      </div>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-4 backdrop-blur-xl">
            {p.preview ? <img src={p.preview} alt="" className="h-14 w-14 rounded-lg border border-blue-500/20 object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-blue-500/20 bg-blue-500/5"><Store size={20} className="text-blue-500/40" /></div>}
            <div className="min-w-0 flex-1">
              <p className="truncate font-body text-sm text-white">{p.title} {p.boosted && <span className="ml-1 rounded-full border border-orange-400/40 bg-orange-500/15 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-orange-200">Vedette</span>}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{p.category} · {p.sales} vente(s)</p>
            </div>
            <div className="text-right">
              <p className="font-display text-lg font-800 tracking-tighter text-white">${p.price}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-300">net +${(p.revenue || 0).toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
};

const PaymentsTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { accountPayments().then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (items === null) return <Loader2 className="mx-auto my-16 animate-spin text-blue-400" />;
  if (items.length === 0) return <Empty text="Aucune transaction pour l'instant." />;
  const STATUS = { paid: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10", pending: "text-amber-300 border-amber-400/40 bg-amber-400/10", failed: "text-red-300 border-red-400/40 bg-red-400/10", expired: "text-[#8AA0C0] border-blue-500/20 bg-blue-500/5" };
  return (
    <div className="space-y-3" data-testid="account-payments">
      {items.map((p) => (
        <div key={p.session_id} className="flex items-center justify-between gap-4 rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-4 backdrop-blur-xl">
          <div className="min-w-0">
            <p className="truncate font-body text-sm text-white">{p.lookup_key || "Transaction"}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{p.created_at ? new Date(p.created_at).toLocaleString("fr-FR") : ""}</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-display text-base font-800 tracking-tighter text-white">
              {((p.amount || 0) / 100).toFixed(2)} {(p.currency || "usd").toUpperCase()}
            </span>
            <span className={`rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${STATUS[p.payment_status] || STATUS.expired}`}>
              {p.payment_status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Portail affilié Rewardful. Remplacer par l'URL exacte du programme Rewardful d'AIBA.
const REWARDFUL_SIGNUP_URL = process.env.REACT_APP_REWARDFUL_AFFILIATE_URL || "https://app.getrewardful.com/signup";

const AffiliateTab = () => {
  return (
    <div className="grid gap-6 lg:grid-cols-5" data-testid="affiliate-tab">
      <div className="lg:col-span-3 rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-7 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-400/40 bg-blue-500/15">
            <Gift size={20} className="text-blue-200" />
          </div>
          <div>
            <h3 className="font-display text-lg font-700 tracking-tight text-white">Programme d'affiliation</h3>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-300/70">Propulsé par Rewardful · 15% récurrent</p>
          </div>
        </div>
        <p className="mt-5 font-body text-sm leading-relaxed text-[#8AA0C0]">
          Le programme d'affiliation AIBA est géré par <span className="text-blue-200">Rewardful</span>. Inscrivez-vous, recevez votre lien de partage unique <span className="font-mono text-blue-200">?via=…</span>, et touchez une commission sur chaque abonnement souscrit via votre lien. Le suivi des clics, inscriptions et commissions se fait automatiquement dans votre tableau de bord Rewardful.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href={REWARDFUL_SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="affiliate-join-btn"
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500">
            <Gift size={13} /> Devenir affilié
          </a>
          <a href="https://app.getrewardful.com/login" target="_blank" rel="noopener noreferrer" data-testid="affiliate-dashboard-btn"
            className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#CBD5E1] hover:text-white">
            <ExternalLink size={13} /> Mon tableau de bord
          </a>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-6">
        <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-7 backdrop-blur-xl">
          <h4 className="font-display text-base font-700 tracking-tight text-white">Comment ça marche</h4>
          <ol className="mt-5 space-y-4">
            {[
              "Inscrivez-vous au programme via Rewardful.",
              "Récupérez votre lien de partage unique (?via=…).",
              "Partagez-le sur vos réseaux, votre blog, vos e-mails.",
              "Chaque abonnement souscrit via votre lien vous rapporte une commission, suivie automatiquement.",
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-blue-400/40 bg-blue-500/10 font-mono text-[10px] text-blue-200">{i + 1}</span>
                <span className="font-body text-sm text-[#8AA0C0]">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

const Empty = ({ text }) => (
  <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#405060]">{text}</p>
);

const PLAN_BADGE = {
  Gratuit: "text-[#8AA0C0] border-blue-500/20 bg-blue-500/5",
  Débutant: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  Expert: "text-blue-300 border-blue-400/40 bg-blue-400/10",
  Premium: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  Pro: "text-fuchsia-300 border-fuchsia-400/40 bg-fuchsia-400/10",
};

const AdminTab = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminStats().then(({ data }) => setStats(data)).catch(() => toast.error("Impossible de charger les statistiques."));
  }, []);

  if (!stats) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-blue-400" size={26} /></div>;
  }

  const maxSignup = Math.max(1, ...stats.signups_14d.map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Membres" value={stats.members.total} testid="admin-total-members" />
        <StatCard icon={TrendingUp} label="Nouveaux (30 j)" value={stats.members.new_30d} testid="admin-new-members" />
        <StatCard icon={Receipt} label="Revenu total" value={`$${stats.revenue.total_usd.toFixed(2)}`} testid="admin-revenue" />
        <StatCard icon={Wallet} label="Crédits en circulation" value={stats.credits.total_in_circulation.toLocaleString("fr-FR")} testid="admin-credits" />
      </div>

      {/* Répartition par plan */}
      <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-6 backdrop-blur-xl" data-testid="admin-by-plan">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Répartition par abonnement</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(stats.members.by_plan).map(([plan, count]) => (
            <span key={plan} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] ${PLAN_BADGE[plan] || PLAN_BADGE.Gratuit}`}>
              <Crown size={12} /> {plan} · {count}
            </span>
          ))}
        </div>
      </div>

      {/* Contenu de la plateforme */}
      <div>
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Contenu de la plateforme</p>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard icon={Film} label="Films" value={stats.content.films} testid="admin-films" />
          <StatCard icon={MessageSquare} label="Posts" value={stats.content.posts} testid="admin-posts" />
          <StatCard icon={Store} label="Produits" value={stats.content.products} testid="admin-products" />
          <StatCard icon={Sparkles} label="Générations" value={stats.content.generations} testid="admin-generations" />
          <StatCard icon={Flag} label="Posts signalés" value={stats.content.reported_posts} testid="admin-reported" />
        </div>
      </div>

      {/* Inscriptions 14 jours */}
      <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-6 backdrop-blur-xl" data-testid="admin-signups-chart">
        <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Inscriptions · 14 derniers jours</p>
        <div className="flex h-40 items-end gap-1.5">
          {stats.signups_14d.map((s, i) => (
            <div key={i} className="group flex flex-1 flex-col items-center gap-2">
              <div className="relative flex w-full flex-1 items-end">
                <div
                  className="w-full rounded-t bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-300 group-hover:from-blue-500 group-hover:to-blue-300"
                  style={{ height: `${(s.count / maxSignup) * 100}%`, minHeight: s.count > 0 ? "6px" : "2px", opacity: s.count > 0 ? 1 : 0.25 }}
                  title={`${s.count} inscription(s)`}
                />
              </div>
              <span className="font-mono text-[8px] text-[#8AA0C0]">{s.date}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Membres récents */}
      <div className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-6 backdrop-blur-xl" data-testid="admin-recent-members">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Membres récents</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-blue-500/15 font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">
                <th className="pb-3 pr-4">Membre</th>
                <th className="pb-3 pr-4">Plan</th>
                <th className="pb-3 pr-4">Crédits</th>
                <th className="pb-3">Inscrit le</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_members.map((m, i) => (
                <tr key={i} data-testid={`admin-member-row-${i}`} className="border-b border-blue-500/5 font-body text-sm text-[#CBD5E1]">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="font-600 text-white">{m.name}</span>
                      {m.role === "admin" && <ShieldCheck size={13} className="text-amber-300" />}
                    </div>
                    <span className="font-mono text-[9px] text-[#8AA0C0]">{m.email}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em] ${PLAN_BADGE[m.plan] || PLAN_BADGE.Gratuit}`}>{m.plan}</span>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs">{(m.credits || 0).toLocaleString("fr-FR")}</td>
                  <td className="py-3 font-mono text-[10px] text-[#8AA0C0]">{m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("profile");

  const load = () => accountOverview().then(({ data }) => setOverview(data)).catch(() => {});

  useEffect(() => {
    if (user === false) navigate("/");
    if (user) load();
  }, [user, navigate]);

  if (user === null || !overview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F172A]">
        <Loader2 className="animate-spin text-blue-400" size={28} />
      </div>
    );
  }

  const tierClass = TIER_COLOR[overview.plan_label] || TIER_COLOR.Gratuit;
  const visibleTabs = user?.role === "admin"
    ? [...TABS, { key: "admin", label: "Admin", icon: ShieldCheck }]
    : TABS;

  return (
    <div className="relative min-h-screen bg-[#0F172A]" data-testid="account-page">
      <div className="lab-fixed-bg" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-5xl px-6 py-16 lg:px-10">
        <div className="mb-10 flex items-center justify-between">
          <Link to="/" data-testid="account-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-200 transition-colors hover:text-white">
            <ArrowLeft size={14} /> Retour
          </Link>
          <div className="flex items-center gap-2">
            <AibaLogo size={26} />
            <span className="font-display text-lg font-800 tracking-tighter text-white">AIBA</span>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-500/10 to-transparent p-7 backdrop-blur-xl">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {user.picture ? (
                <img src={user.picture} alt="avatar" className="h-16 w-16 rounded-2xl border border-blue-400/40 object-cover" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-blue-400/40 bg-blue-500/15">
                  <User size={26} className="text-blue-200" />
                </div>
              )}
              <div>
                <h1 className="font-display text-2xl font-800 tracking-tighter text-white" data-testid="account-name">{user.name || user.email}</h1>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">{user.email}</p>
                <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${tierClass}`} data-testid="account-plan">
                  <Crown size={10} /> Abonnement {overview.plan_label}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl border border-blue-400/40 bg-blue-500/10 px-5 py-3 text-center" data-testid="account-credits">
                <p className="font-display text-2xl font-800 tracking-tighter text-white">{user.credits}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200">crédits</p>
              </div>
              <button data-testid="account-logout" onClick={() => { logout(); navigate("/"); }}
                className="flex items-center gap-2 rounded-full border border-blue-500/20 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#CBD5E1] hover:text-white">
                <LogOut size={13} /> Déconnexion
              </button>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard icon={Sparkles} label="Générations" value={overview.stats.generations} testid="stat-generations" />
            <StatCard icon={ShoppingBag} label="Achats" value={overview.stats.purchases} testid="stat-purchases" />
            <StatCard icon={Store} label="Produits en vente" value={overview.stats.products} testid="stat-products" />
            <StatCard icon={Receipt} label="Paiements" value={overview.stats.payments} testid="stat-payments" />
          </div>
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-blue-500/15 pb-1">
          {visibleTabs.map((tb) => {
            const Icon = tb.icon;
            return (
              <button key={tb.key} data-testid={`account-tab-${tb.key}`} onClick={() => setTab(tb.key)}
                className={`inline-flex items-center gap-2 rounded-t-xl px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  tab === tb.key ? "border-b-2 border-blue-400 text-white" : "text-[#8AA0C0] hover:text-blue-200"
                }`}>
                <Icon size={13} /> {tb.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {tab === "profile" && <ProfileTab overview={overview} reload={load} />}
          {tab === "generations" && <GenerationsTab />}
          {tab === "purchases" && <PurchasesTab />}
          {tab === "products" && <ProductsTab />}
          {tab === "affiliation" && <AffiliateTab />}
          {tab === "payments" && <PaymentsTab />}
          {tab === "admin" && user?.role === "admin" && <AdminTab />}
        </div>
      </div>
    </div>
  );
}
